import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy, Check, Heart, Share2, BadgeCheck } from "lucide-react";
import CustomCursor from "@/components/CustomCursor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTRAIT = /\/shorts\//;

const ytId = (url) => {
  const m = url.match(/(?:youtu\.be\/|shorts\/|embed\/|watch\?v=|v\/)([\w-]{11})/) || url.match(/^([\w-]{11})$/);
  return m ? m[1] : null;
};

const likedSet = () => new Set(JSON.parse(localStorage.getItem("liked_videos") || "[]"));

export default function VideoPostPage() {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/videos/${videoId}`)
      .then((r) => {
        setVideo(r.data);
        setLiked(likedSet().has(videoId));
      })
      .catch(() => setNotFound(true));
  }, [videoId]);

  const toggleLike = async () => {
    const next = !liked;
    setLiked(next);
    setVideo((v) => ({ ...v, likes: Math.max(0, v.likes + (next ? 1 : -1)) }));
    const set = likedSet();
    next ? set.add(videoId) : set.delete(videoId);
    localStorage.setItem("liked_videos", JSON.stringify([...set]));
    try {
      const { data } = await axios.post(`${API}/videos/${videoId}/like`, { action: next ? "like" : "unlike" });
      setVideo((v) => ({ ...v, likes: data.likes }));
    } catch {}
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: video.title, text: `${video.title} — AI video by Nayan Verma`, url });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share it anywhere.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(video.prompt);
      setCopied(true);
      toast.success("Prompt copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Use the download instead.");
    }
  };

  const downloadPrompt = () => {
    const blob = new Blob([`${video.title}\n\nPrompt by Nayan Verma — nayanverma.com\n\n${video.prompt}`], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${video.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}-prompt.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Prompt downloaded. Create some magic.");
  };

  if (notFound)
    return (
      <div className="bg-[#EFEAE1] min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="video-post-not-found">
        <p className="font-giant text-3xl text-[#121110]">This post has moved on.</p>
        <Link to="/content" className="pill pill--solid">Browse all AI videos</Link>
      </div>
    );

  if (!video)
    return <div className="bg-[#EFEAE1] min-h-screen flex items-center justify-center text-[#4A453F]">Loading…</div>;

  const id = ytId(video.youtube_url);
  const portrait = PORTRAIT.test(video.youtube_url);

  return (
    <div className="relative bg-[#EFEAE1] text-[#121110] min-h-screen cursor-hidden" data-testid="video-post-page">
      <CustomCursor />
      <header className="fixed top-0 left-0 right-0 z-50 px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/content" className="pill inline-flex items-center gap-2" data-testid="post-back-link">
          <ArrowLeft size={14} /> All AI videos
        </Link>
        <a href="/#contact" className="pill pill--solid" data-testid="post-contact-cta">Contact</a>
      </header>

      <main className="pt-24 lg:pt-28 pb-24 px-5 flex justify-center">
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className={`w-full ${portrait ? "max-w-[460px]" : "max-w-3xl"} border border-[#121110]/15 rounded-3xl overflow-hidden bg-[#FDFBF7] shadow-[0_24px_60px_-30px_rgba(18,17,16,0.35)]`}
          data-testid="video-post-card"
        >
          <div className="flex items-center gap-3 px-5 py-4">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_nayan-magic/artifacts/5ab5jyxm_Single_picture_of_this-1779351362852.png"
              alt="Nayan Verma"
              className="w-10 h-10 rounded-full object-cover"
              style={{ objectPosition: "50% 12%" }}
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm flex items-center gap-1.5">
                Nayan Verma <BadgeCheck size={15} className="text-[#A8512E]" />
              </p>
              <p className="text-xs text-[#4A453F]">AI video · nayanverma.com</p>
            </div>
          </div>

          <div className={`relative bg-[#121110] ${portrait ? "aspect-[9/16]" : "aspect-video"}`}>
            {id ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${id}`}
                title={video.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="post-video-embed"
              />
            ) : (
              <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center text-[#EFEAE1]">
                Watch on YouTube
              </a>
            )}
          </div>

          <div className="px-5 py-4">
            <div className="flex items-center gap-5">
              <button onClick={toggleLike} className="flex items-center gap-2 group" data-testid="post-like-button" aria-label="Like">
                <Heart
                  size={26}
                  className={`transition-all duration-300 group-active:scale-75 ${liked ? "fill-[#A8512E] text-[#A8512E]" : "text-[#121110] group-hover:text-[#A8512E]"}`}
                />
              </button>
              <button onClick={share} className="flex items-center gap-2 group" data-testid="post-share-button" aria-label="Share">
                <Share2 size={24} className="text-[#121110] transition-colors duration-300 group-hover:text-[#A8512E] group-active:scale-75" />
              </button>
            </div>
            <p className="font-semibold text-sm mt-3" data-testid="post-like-count">
              {video.likes.toLocaleString()} {video.likes === 1 ? "like" : "likes"}
            </p>
            <p className="mt-2 text-[15px] leading-relaxed">
              <span className="font-semibold">nayanverma</span>{" "}
              <span data-testid="post-title">{video.title}</span>
            </p>

            {video.pdf_name ? (
              <div className="mt-5 pt-5 border-t border-[#121110]/10 flex flex-wrap gap-3">
                <a href={`${API}/videos/${video.id}/pdf`} className="pill pill--solid inline-flex items-center gap-2 text-sm px-6 py-3" data-testid="post-pdf-download-button">
                  <Download size={15} /> Download PDF
                </a>
              </div>
            ) : (
              video.prompt && (
                <div className="mt-5 pt-5 border-t border-[#121110]/10 flex flex-wrap gap-3">
                  <button onClick={downloadPrompt} className="pill pill--solid inline-flex items-center gap-2 text-sm px-6 py-3" data-testid="post-download-button">
                    <Download size={15} /> Download prompt
                  </button>
                  <button onClick={copyPrompt} className="pill inline-flex items-center gap-2 text-sm px-6 py-3" data-testid="post-copy-button">
                    {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy prompt"}
                  </button>
                </div>
              )
            )}
          </div>
        </motion.article>
      </main>
    </div>
  );
}
